export type WarningType =
  | 'heading-skip'
  | 'generic-link-text'
  | 'empty-alt'
  | 'empty-link'
  | 'image-link-no-alt'
  | 'dead-link'
  | 'new-tab-no-warning'
  | 'duplicate-link-text'
  | 'table-no-headers'
  | 'table-empty-header'
  | 'table-no-caption'
  | 'table-merged-no-headers'
  | 'table-nested'
  | 'table-auto-fixed'

export interface AccessibilityWarning {
  type: WarningType
  message: string
  context?: string
}

export interface ProcessResult {
  html: string
  warnings: AccessibilityWarning[]
}

const GENERIC_LINK_TEXTS = new Set([
  'click here', 'here', 'read more', 'learn more',
  'this link', 'more', 'click', 'link', 'details',
])

export const AUTO_FIXABLE_TYPES = new Set<WarningType>(['new-tab-no-warning', 'table-no-caption'])

export const A11Y_SUGGESTIONS: Record<WarningType, string> = {
  'heading-skip': 'Change the heading level to maintain a logical hierarchy — no level skipping',
  'empty-alt': 'Double-click the image to add descriptive alt text, or mark it as decorative',
  'empty-link': 'Add descriptive text inside the link, or remove it if it serves no purpose',
  'image-link-no-alt': 'Add alt text to the image describing where the link goes',
  'dead-link': "Replace '#' or 'javascript:' with a real URL, or remove the link",
  'generic-link-text': "Replace vague text like 'click here' with text describing the destination",
  'duplicate-link-text': "Make link text unique by adding context, e.g. 'Annual report 2024'",
  'table-no-headers': 'Add <th> header cells to identify each column or row',
  'table-empty-header': 'Add descriptive text to all empty <th> cells',
  'table-no-caption': 'Click "Fix All" to add a placeholder caption, then update it',
  'table-merged-no-headers': "Add headers='id1 id2' to merged cells linking to relevant <th> IDs",
  'table-nested': 'Replace nested tables with a flat structure or CSS layout',
  'table-auto-fixed': 'Review the auto-corrected table structure to verify it looks correct',
  'new-tab-no-warning': 'Click "Fix All" to auto-add an aria-label notifying users this link opens in a new tab',
}

export function processHtmlForA11y(html: string, options?: {applyAllFixes?: boolean}): ProcessResult {
  if (!html?.trim()) return { html: html ?? '', warnings: [] }

  const parser = new DOMParser()
  const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html')
  const body = doc.body
  const warnings: AccessibilityWarning[] = []

  // Fix: Strip TinyMCE inline a11y warning classes/titles added by the editor UI
  const EDITOR_WARNING_CLASSES = ['tinymce-a11y-warn-heading', 'tinymce-a11y-warn-link']
  EDITOR_WARNING_CLASSES.forEach(cls => {
    body.querySelectorAll(`.${cls}`).forEach(el => {
      el.classList.remove(cls)
      el.removeAttribute('title')
      if (el.getAttribute('class') === '') el.removeAttribute('class')
    })
  })

  // Fix: <b> → <strong>
  body.querySelectorAll('b').forEach(el => {
    const strong = doc.createElement('strong')
    strong.innerHTML = el.innerHTML
    el.replaceWith(strong)
  })

  // Fix: <i> → <em> (skip icon elements with class)
  body.querySelectorAll('i:not([class])').forEach(el => {
    const em = doc.createElement('em')
    em.innerHTML = el.innerHTML
    el.replaceWith(em)
  })

  // Fix: Remove empty <p> tags
  body.querySelectorAll('p').forEach(el => {
    const isEmpty = !el.textContent?.replace(/[\s ]+/g, '') && !el.querySelector('img')
    if (isEmpty) el.remove()
  })

  // Fix: Add rel to target="_blank" links
  body.querySelectorAll('a[target="_blank"]').forEach(el => {
    const rel = el.getAttribute('rel') ?? ''
    if (!rel.includes('noopener') || !rel.includes('noreferrer')) {
      const parts = rel ? rel.split(/\s+/).filter(Boolean) : []
      if (!parts.includes('noopener')) parts.push('noopener')
      if (!parts.includes('noreferrer')) parts.push('noreferrer')
      el.setAttribute('rel', parts.join(' '))
    }
  })

  // Warn: Dead links — href="#", empty href, javascript:
  body.querySelectorAll('a[href]').forEach(el => {
    const href = (el.getAttribute('href') ?? '').trim()
    if (href === '#' || href === '' || href.toLowerCase().startsWith('javascript:')) {
      warnings.push({
        type: 'dead-link',
        message: 'Link goes nowhere — replace "#" or "javascript:" with a real URL',
        context: el.outerHTML.slice(0, 120),
      })
    }
  })



  // Fix + Warn: Table accessibility
  body.querySelectorAll('table').forEach(table => {
    // Warn: nested table (non-blocking)
    if (table.closest('td, th')) {
      warnings.push({
        type: 'table-nested',
        message: 'Nested table detected — use a flat table structure instead',
        context: table.outerHTML.slice(0, 120),
      })
    }

    const ths = table.querySelectorAll('th')
    const tds = table.querySelectorAll('td')

    // Fix (when applyAllFixes): add placeholder caption — runs before early return so all tables get it
    if (options?.applyAllFixes && !table.querySelector('caption')) {
      const caption = doc.createElement('caption')
      caption.textContent = 'Table description — update this caption'
      table.insertBefore(caption, table.firstChild)
    }

    // Warn: no <caption> (non-blocking) — checked before early return so headerless tables also warn
    if (!table.querySelector('caption')) {
      warnings.push({
        type: 'table-no-caption',
        message: 'Table has no caption — add a <caption> element describing what the table contains',
        context: table.outerHTML.slice(0, 120),
      })
    }

    // Warn (blocking): no header cells at all — after caption so table-no-caption always fires
    if (ths.length === 0 && tds.length > 0) {
      warnings.push({
        type: 'table-no-headers',
        message: 'Table has no header cells — add <th> elements to identify columns or rows',
        context: table.outerHTML.slice(0, 120),
      })
      return
    }

    // Warn (blocking): empty <th> cells
    ths.forEach(th => {
      if (!th.textContent?.trim()) {
        warnings.push({
          type: 'table-empty-header',
          message: 'Table header cell is empty — add text describing the column or row',
          context: th.outerHTML.slice(0, 120),
        })
      }
    })

    // Warn: merged cells without headers attribute (non-blocking)
    table.querySelectorAll('td[colspan], td[rowspan], th[colspan], th[rowspan]').forEach(cell => {
      const colspan = parseInt(cell.getAttribute('colspan') ?? '1', 10)
      const rowspan = parseInt(cell.getAttribute('rowspan') ?? '1', 10)
      if ((colspan > 1 || rowspan > 1) && !cell.hasAttribute('headers')) {
        warnings.push({
          type: 'table-merged-no-headers',
          message: 'Merged cell is missing a headers attribute — add headers="id1 id2" linking to the relevant <th> cells',
          context: cell.outerHTML.slice(0, 120),
        })
      }
    })

    // Fix + Warn: promote first all-th row to thead and add scope attributes
    const firstRow = table.querySelector('tr')
    if (!firstRow) return
    const firstRowCells = Array.from(firstRow.querySelectorAll('td, th'))
    const allTh = firstRowCells.length > 0 && firstRowCells.every(c => c.tagName === 'TH')
    let autoFixed = false

    if (allTh && !firstRow.closest('thead') && firstRow.parentNode) {
      const thead = doc.createElement('thead')
      firstRow.parentNode.insertBefore(thead, firstRow)
      thead.appendChild(firstRow)
      autoFixed = true
    }

    table.querySelectorAll('thead th').forEach(th => {
      if (!th.hasAttribute('scope')) {
        th.setAttribute('scope', 'col')
        autoFixed = true
      }
    })

    table.querySelectorAll('tbody tr').forEach(row => {
      const firstTh = row.querySelector('th')
      if (firstTh && !firstTh.hasAttribute('scope')) {
        firstTh.setAttribute('scope', 'row')
        autoFixed = true
      }
    })

    if (autoFixed) {
      warnings.push({
        type: 'table-auto-fixed',
        message: 'Table structure was auto-corrected (<thead> promoted, scope attributes added) — verify it looks correct',
        context: table.outerHTML.slice(0, 120),
      })
    }
  })

  // Fix: De-duplicate id attributes
  const existingIds = new Set(
    Array.from(body.querySelectorAll('[id]')).map(el => el.id)
  )
  const idCount = new Map<string, number>()
  body.querySelectorAll('[id]').forEach(el => {
    const id = el.id
    const count = idCount.get(id) ?? 0
    if (count > 0) {
      let suffix = count + 1
      while (existingIds.has(`${id}-${suffix}`)) suffix++
      const newId = `${id}-${suffix}`
      existingIds.add(newId)
      el.id = newId
    }
    idCount.set(id, count + 1)
  })

  // Warn: Heading level skip
  const headings = Array.from(body.querySelectorAll('h1,h2,h3,h4,h5,h6'))
  for (let i = 1; i < headings.length; i++) {
    const prev = parseInt(headings[i - 1].tagName[1], 10)
    const curr = parseInt(headings[i].tagName[1], 10)
    if (curr - prev > 1) {
      warnings.push({
        type: 'heading-skip',
        message: `Heading jumps from H${prev} to H${curr} — consider H${prev + 1}`,
        context: headings[i].outerHTML.slice(0, 120),
      })
    }
  }

  // Fix (when applyAllFixes): new-tab-no-warning — add aria-label
  if (options?.applyAllFixes) {
    body.querySelectorAll('a[target="_blank"]').forEach(el => {
      const text = (el.textContent ?? '').trim()
      const ariaLabel = (el.getAttribute('aria-label') ?? '').toLowerCase()
      const title = (el.getAttribute('title') ?? '').toLowerCase()
      const phrases = ['new tab', 'new window', 'opens in']
      const hasNotice = phrases.some(p => text.toLowerCase().includes(p) || ariaLabel.includes(p) || title.includes(p))
      if (!hasNotice) {
        const labelText = el.getAttribute('aria-label') || text || 'Link'
        el.setAttribute('aria-label', `${labelText} (opens in new tab)`)
      }
    })
  }

  // Warn: target="_blank" without user notice in text/aria-label/title
  body.querySelectorAll('a[target="_blank"]').forEach(el => {
    const text = (el.textContent ?? '').toLowerCase()
    const ariaLabel = (el.getAttribute('aria-label') ?? '').toLowerCase()
    const title = (el.getAttribute('title') ?? '').toLowerCase()
    const phrases = ['new tab', 'new window', 'opens in']
    const hasNotice = phrases.some(p => text.includes(p) || ariaLabel.includes(p) || title.includes(p))
    if (!hasNotice) {
      warnings.push({
        type: 'new-tab-no-warning',
        message: 'Link opens in a new tab without warning users — add "(opens in new tab)" to the text or aria-label',
        context: el.outerHTML.slice(0, 120),
      })
    }
  })

  // Warn: Same visible link text going to different destinations
  const linksByText = new Map<string, Set<string>>()
  body.querySelectorAll('a[href]').forEach(el => {
    const text = (el.textContent?.trim() ?? '').toLowerCase()
    const href = el.getAttribute('href') ?? ''
    if (!text || GENERIC_LINK_TEXTS.has(text)) return
    if (!linksByText.has(text)) linksByText.set(text, new Set())
    linksByText.get(text)!.add(href)
  })
  linksByText.forEach((hrefs, text) => {
    if (hrefs.size > 1) {
      warnings.push({
        type: 'duplicate-link-text',
        message: `Multiple links say "${text}" but go to different pages — make each link text unique`,
        context: Array.from(hrefs).join(', ').slice(0, 120),
      })
    }
  })

  // Warn: Generic or empty link text, and image links with no alt
  body.querySelectorAll('a').forEach(el => {
    const text = el.textContent?.trim() ?? ''
    const imgChild = el.querySelector('img')
    if (!text && !imgChild) {
      warnings.push({
        type: 'empty-link',
        message: 'A link has no text — add descriptive link text',
        context: el.outerHTML.slice(0, 120),
      })
    } else if (!text && imgChild) {
      const badAlt = !imgChild.getAttribute('alt') || imgChild.getAttribute('alt') === ''
      if (badAlt) {
        warnings.push({
          type: 'image-link-no-alt',
          message: 'Image inside a link needs alt text describing the link destination',
          context: el.outerHTML.slice(0, 120),
        })
      }
    } else if (GENERIC_LINK_TEXTS.has(text.toLowerCase())) {
      warnings.push({
        type: 'generic-link-text',
        message: `Link "${text}" doesn't describe its destination`,
        context: el.outerHTML.slice(0, 120),
      })
    }
  })

  // Warn: <img> with no alt attribute at all (blocking)
  body.querySelectorAll('img:not([alt])').forEach(el => {
    warnings.push({
      type: 'empty-alt',
      message: 'Image is missing alt text — add a description or mark as decorative',
      context: el.outerHTML.slice(0, 120),
    })
  })

  // Warn: <img> with explicit empty alt not marked decorative (blocking)
  body.querySelectorAll('img[alt=""]').forEach(el => {
    if (!el.hasAttribute('data-decorative')) {
      warnings.push({
        type: 'empty-alt',
        message: 'Image has empty alt text — add a description or mark as decorative',
        context: el.outerHTML.slice(0, 120),
      })
    }
  })

  // Fix: Strip internal data-decorative="auto" marker — was added by old auto-fix, not valid HTML
  body.querySelectorAll('img[data-decorative="auto"]').forEach(el => {
    el.removeAttribute('data-decorative')
  })

  return { html: body.innerHTML, warnings }
}
